import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { TextField, Button } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { JobSearchParams } from '../../services/job-search-service';
import * as H from 'history';
import AutoSuggestGeocoder from '../AutoSuggestGeocoder';
import useSearchField from '../../hooks/useSearchField';
import { GoogleGeocoder, SimpleGeocodeResult } from '../../services/geo/geocoder';
import { lightTint, midTint, darkTint, paleAmber, darkBrown } from '../../theme';
import useTitle from '../../hooks/useTitle';
import { Dispatch } from 'react';
import useSearchFieldAndInput from '../../hooks/useSearchFieldAndInput';
// import withStyles from '@material-ui/core/styles/withStyles';

const useStyles = makeStyles({
    root: {
        marginBottom: 8,
        textAlign: 'center',

        // '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
        //     borderColor: lightTint
        // }

        'body.with-map &': {
            position: 'absolute',
            // top: -75
            top: 64,    // header height
            transform: 'translateY(-160px)',
            transition: 'transform 300ms ease-in',
        },

        '@media (max-width: 500px)': {
            'body.with-map &': {
                transform: 'translateY(-260px)',
            }
        }
    },
    // textField: {
    //     width: 240,
    // },
    // actions: {
    //     // marginTop: 12
    // }
}, {name: 'job-search-form'});

const textFieldWidth = 240;

const textFieldStyles = {
    root: {
        width: textFieldWidth,
        transition: 'transform 300ms ease-out',

        '& label.Mui-focused': {
            // color: '#805b00' // 'rgba(0, 0, 0, 0.87)',
            color: darkTint
        },
        '& input, & legend': {
            backgroundColor: paleAmber,  // light variant of theme feba00 used by Chrome
        },
        '& input, & input:-internal-autofill-selected': {
            // backgroundColor: '#FFC107',  // $default-primary-color
            color: darkBrown,
            backgroundColor: paleAmber,  // light variant of theme feba00 used by Chrome
            '-webkit-box-shadow': `0 0 0 30px ${paleAmber} inset !important`,
            // '-webkit-text-fill-color': paleAmber,
            borderRadius: 4
        },
        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderColor: lightTint
        },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            // borderColor: 'rgba(0, 0, 0, 0.5)',
            // borderColor: '#805b00',
            borderColor: midTint
        },
        // '&.Mui-focused fieldset': {
        //     // borderColor: 'rgba(0, 0, 0, 0.23)'
        //     // borderColor: 'red'
        // },
        // '& .MuiOutlinedInput-root': {
        // },
    }
};

const useTextFieldStyles = makeStyles(textFieldStyles);
// const SearchField = withStyles(textFieldStyles)(TextField);

interface JobSearchFormProps {
    params: JobSearchParams;
    history: H.History;
    autoSubmit?: boolean;
    dispatch: Dispatch<SearchFormAction>;
}

export interface SearchAction {
    type: 'search' | 'params';
    payload: JobSearchParams;
}
export type SearchFormAction = SearchAction;

function JobSearchForm({params, autoSubmit, dispatch, history}: JobSearchFormProps) {
    const [query, onQuery] = useSearchFieldAndInput('query', params);
    const [location, onLocation] = useSearchFieldAndInput('location', params);
    const [country, onCountry] = useSearchFieldAndInput('country', params);
    // NLP recognises "city" - we turn this into "location" (`city, state` + country)
    const [city] = useSearchField('city');
    // NLP recognises other slots which we stitch together into the query...
    // careerLevel: {graduate: 'graduate', 'intern', junior, 'mid-level', senior: ['lead', senior]}
    // skill: ""
    // jobTitle: "accountant"
    // industry: {'information technology': [IT, computers, technology], sales, 'customer service'}
    // reason: {'life style': ['work from home'], new: ['better', 'cool', 'new'], pay: ['well paying']}
    // const [careerLevel] = useSearchField('careerLevel');
    // const [skill] = useSearchField('skill');
    // const [jobTitle] = useSearchField('jobTitle');
    // const [industry] = useSearchField('industry');

    const classes = useStyles();
    const textFieldClasses = useTextFieldStyles();

    // console.info(' rendering JobSearchForm', query, location, country, autoSubmit);

    const setTitle = useTitle('Job Search');

    /** When NLP recognises a city, use the geocoder to update the state and country */
    useEffect(() => {
        let q = query;

        /*if (!query) {
            // q = careerLevel;
            if (skill) {
                q = skill;
            }
            if (jobTitle) {
                if (q) {
                    q += ' ' + jobTitle;
                } else {
                    q = jobTitle;
                }
            }
            if (!q) {
                q = industry;
            }
            if (q) {
                onQuery({target: {value: q}});
            }
        }*/

        if (city && !location) {
            new GoogleGeocoder().geocode(city, country).then(results => {
                // tslint:disable-next-line:no-shadowed-variable
                const [location, country] = onLocationSelected(results[0]);
                updateParams(q, location, country);
                updateSearch(q, location, country, 'replace');
            });
        // } else if (q && !query) {
        //     updateParams(q, location, country);
        //     updateSearch(q, location, country, 'replace');
        }
    }, []);

    useEffect(() => {
        // console.info('should we submit now?');
        if (query || location) {
            if (autoSubmit) { // window.location.search.match(/(query|location)=/)) {
                // console.info('...yes, autosubmit');
                _onSubmit();
            } else {
                // console.info('...no, just update params');
                updateParams(query, location, country);
                dispatch({type: 'params', payload: params});
            }
        }
    }, [autoSubmit, query, location]);

    function onLocationSelected(selection: SimpleGeocodeResult) {
        // console.info('location selected:', selection);
        const locationStr = selection.location.suburb + ', ' + selection.location.state;
        return [onLocation({target: {value: locationStr}}),
                onCountry({target: {value: selection.location.country!}})];
    }

    // tslint:disable-next-line:no-shadowed-variable
    function updateParams(query: string | undefined, location: string | undefined, country: string | undefined) {
        let title = '';
        if (query) {
            params.query = query;
            title = query + ' ';
        }
        title += 'jobs';
        if (location) {
            params.location = location;
            title += ' in ' + location;
        }
        if (country) {
            params.country = country;
        }

        // TODO: get title from and/or update profile name
        setTitle(title);
    }

    // tslint:disable-next-line:no-shadowed-variable
    function updateSearch(query: string | undefined, location: string | undefined, country: string | undefined, operation: string = 'push') {
        const originalSearch = history.location.search;
        let search = [];
        if (query) {
            search.push('query=' + encodeURIComponent(query));
        }
        if (location) {
            search.push('location=' + encodeURIComponent(location));
        }
        if (country) {
            search.push('country=' + country);
        }
        if (search.length) {
            const newSearch = '?' + search.join('&');
            if (newSearch !== originalSearch) {
                console.info(originalSearch, '->', newSearch);
                const update = operation === 'push' ? history.push : history.replace;
                update({...history.location, search: search.join('&')});
            }
        }
    }

    function _onSubmit(e?: React.MouseEvent) {
        if (e) {
            e.preventDefault();
        }

        updateSearch(query, location, country);
        updateParams(query, location, country);
        dispatch({type: 'search', payload: params});
    }

    const searchField = useMemo(() => {
        // console.info('  updating searchField', query);
        return (
            <TextField
                id="query"
                label="Search"
                placeholder="Anything"
                helperText="job title, keywords or company name"
                value={query}
                onChange={onQuery}
                variant="outlined"
                margin="normal"
                className={textFieldClasses.root}
                autoFocus
                InputLabelProps={{
                    shrink: true,
                }}
            />
        );
    }, [query]);

    const locationField = useMemo(() => {
        // console.info('  updating locationField', location, country);
        return (
            <AutoSuggestGeocoder
                theme={{container: {textFieldWidth: 240, display: 'inline-flex', position: 'relative'}}}
                country={country}
                value={location || city}
                onSelect={onLocationSelected}
                textFieldProps={{
                    id: 'location',
                    label: 'Location',
                    placeholder: 'Anywhere',
                    helperText: 'city, state/territory or postcode',
                    variant: 'outlined',
                    margin: 'normal',
                    className: textFieldClasses.root,
                    InputLabelProps: {
                        shrink: true,
                    }
                }}
            />
        );
    }, [location, city, country]);


    // TODO: consider: sort by rating, relevance, date, location, pay - probably better to do in a profile
    return (
        <form className={classes.root}>
            {searchField}
            {locationField}
            <div>
                <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    onClick={_onSubmit}
                >
                    Submit
                </Button>
            </div>
        </form>
    );
}

export default JobSearchForm;